/**
 * LES INDICATEURS, CÔTÉ ÉCRAN — lot 103, 18/09/2026.
 *
 * L'API rend les valeurs déjà calculées, période close par période close. Ici :
 * le tri, les filtres, les compteurs et l'ÉCRITURE d'une valeur selon son
 * unité — un pourcentage, des euros et un délai ne se lisent pas pareil.
 */

import {
  compteurs,
  INDICATEURS,
  statutDe,
  tendanceDe,
  type Compteurs,
  type Famille,
  type Indicateur,
  type Statut,
  type Tendance,
  type Unite,
} from '@stafa/core';

export interface CarteLue {
  readonly cle: string;
  readonly valeur: number | null;
  readonly sous_ligne: string | null;
  readonly periode: string;
  readonly serie: readonly {
    readonly periode: string;
    readonly valeur: number | null;
  }[];
  readonly objectif: number | null;
  readonly alerte: number | null;
  readonly manque: string | null;
}

export interface IndicateursLus {
  readonly aujourdhui: string;
  readonly semaine: { readonly debut: string | null; readonly fin: string };
  readonly mois: string | null;
  readonly fenetre_journees: { readonly du: string; readonly au: string };
  readonly cartes: readonly CarteLue[];
}

export interface Carte {
  readonly definition: Indicateur;
  readonly lue: CarteLue;
  readonly statut: Statut;
  readonly tendance: Tendance;
}

export function cartesDe(l: IndicateursLus): Carte[] {
  return INDICATEURS.map((definition) => {
    const lue =
      l.cartes.find((c) => c.cle === definition.cle) ??
      ({
        cle: definition.cle,
        valeur: null,
        sous_ligne: null,
        periode: '',
        serie: [],
        objectif: null,
        alerte: null,
        manque: 'indicateur non calculé',
      } satisfies CarteLue);
    const precedent = lue.serie[lue.serie.length - 2]?.valeur ?? null;
    return {
      definition,
      lue,
      statut: statutDe(lue.valeur, lue.objectif, lue.alerte, definition.sens),
      tendance: tendanceDe(lue.valeur, precedent, definition.sens),
    };
  });
}

export function filtrer(
  cartes: readonly Carte[],
  famille: Famille | 'tout',
  seulementLesEcarts: boolean,
): Carte[] {
  return cartes.filter(
    (c) =>
      (famille === 'tout' || c.definition.famille === famille) &&
      (!seulementLesEcarts || c.statut === 'rouge' || c.statut === 'orange'),
  );
}

export function compteursDe(cartes: readonly Carte[]): Compteurs {
  return compteurs(cartes.map((c) => c.statut));
}

const nombre = (v: number, decimales = 0) =>
  v.toLocaleString('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });

/** Une valeur, écrite selon son unité. */
export function ecrire(valeur: number | null, unite: Unite): string {
  if (valeur === null) return '—';
  switch (unite) {
    case 'euros':
      return `${nombre(Math.round(valeur / 100))} €`;
    case 'pourcent':
      return `${nombre(valeur * 100, 1)} %`;
    case 'jours':
      return `${nombre(valeur, valeur < 10 ? 1 : 0)} j`;
    case 'km':
      return `${nombre(valeur)} km`;
    case 'euro_km':
      return `${nombre(valeur / 100, 2)} €/km`;
    default:
      return nombre(valeur);
  }
}

/**
 * LA SAISIE D'UN SEUIL, dans l'unité que la personne voit. Un pourcentage se
 * saisit en points (« 85 » pour 85 %), un montant en euros, un ratio au
 * kilomètre en euros. L'API, elle, garde la valeur du calcul.
 */
export function versSeuil(saisie: string, unite: Unite): number | null | 'faux' {
  const t = saisie.trim().replace(',', '.').replace(/\s/g, '');
  if (t === '') return null;
  const v = Number(t);
  if (!Number.isFinite(v)) return 'faux';
  if (unite === 'pourcent') return v / 100;
  if (unite === 'euros' || unite === 'euro_km') return Math.round(v * 100);
  return v;
}

export function depuisSeuil(valeur: number | null, unite: Unite): string {
  if (valeur === null) return '';
  if (unite === 'pourcent')
    return String(Math.round(valeur * 1000) / 10).replace('.', ',');
  if (unite === 'euros' || unite === 'euro_km') {
    return String(Math.round(valeur) / 100).replace('.', ',');
  }
  return String(valeur).replace('.', ',');
}

export const NOMS_STATUTS: Readonly<Record<Statut, string>> = {
  vert: 'dans l’objectif',
  orange: 'à surveiller',
  rouge: 'en alerte',
  sans: 'pas d’objectif',
};

/** « S38 » ou « septembre 2026 » selon le rythme. */
export function periodeEnMots(periode: string): string {
  if (/^\d{4}-\d{2}$/.test(periode)) {
    const noms = [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ];
    return `${noms[Number(periode.slice(5, 7)) - 1] ?? periode} ${periode.slice(0, 4)}`;
  }
  return periode === '' ? '—' : `semaine ${periode.replace('S', '')}`;
}
